import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Card, Container, Row, Col, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { post, get, patch } from '../../../../helpers/httpClient';
import { toast } from 'react-toastify';
import {Editor, renderEditorDataToHTML} from '../../../../helpers/Editor';
import PageBasicFields from '../../../../helpers/PageBasicFields';

function ArticleEditor() {
  const navigate = useNavigate();
  const { id, pageId } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = !!pageId;

  const [form, setForm] = useState({ slug: '', title: '', extra: { body: { blocks: [] } } });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!pageId);
  const [editorData, setEditorData] = useState(null);

  const titleText = useMemo(() => (isEdit ? 'Edit Article' : 'New Article'), [isEdit]);

  const handleEditorChange = (data) => {
    // Keep the editor body as an object in state to avoid double stringification
    setForm((prev) => ({ ...prev, extra: { ...(prev.extra || {}), body: data } }));
  };

  const safeParseBody = (val) => {
    if (!val) return { blocks: [] };
    if (typeof val === 'object') return val;
    try {
      const parsed = JSON.parse(val);
      // If server already stored stringified object, parsed is an object
      return typeof parsed === 'object' && parsed !== null ? parsed : { blocks: [] };
    } catch (e) {
      return { blocks: [] };
    }
  };

  useEffect(() => {
    let isMounted = true;
    async function loadPage() {
      if (!isEdit) return;
      try {
        setLoading(true);
        setError('');
        const resp = await get(`pages/${pageId}`);
        const page = resp?.page || resp;
        if (!page) throw new Error('Not found');
        if (!isMounted) return;
        const bodyObj = safeParseBody(page?.extra?.body);
        setForm({
          slug: page.slug || '',
          title: page.title || '',
          preview: page.preview,
          components: page.components || { items: [] },
          extra: { ...(page.extra || {}), body: bodyObj },
        });
        setEditorData({ time: Date.now(), blocks: bodyObj.blocks || [] });
      } catch (e) {
        if (isMounted) toast.error('Failed to load page data.');
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    loadPage();
    return () => { isMounted = false; };
  }, [id, pageId, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.title.trim()) {
      toast.error('Title is required.');
      return;
    }

    const bodyObj = safeParseBody(form?.extra?.body);
    const htmlEditor = JSON.stringify(bodyObj);

    const payload = {
      type: 'article',
      slug: (form.slug || '').trim(),
      title: (form.title || '').trim(),
      preview: form.preview || null,
      components: form.components || [],
      extra: {
          body: htmlEditor,
          rendered: renderEditorDataToHTML(bodyObj),
      },
    };

    try {
      setSaving(true);
      setError('');
      if (isEdit) {
        await patch(`pages/${pageId}`, payload);
      } else {
        const parent = searchParams.get('parent');
        await post(`websites/${id}/pages`, { ...payload, order: 0, parent: parent || null });
      }
      navigate(`/websites/${id}?tab=structure`);
    } catch (err) {
      toast.error(isEdit ? 'Failed to update page.' : 'Failed to create page. Please fix validation issues or try a different slug.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => navigate(-1);

  return (
    <Container className="mt-4">
      <Row>
        <Col md={{ span: 10, offset: 0 }} lg={{ span: 9, offset: 0 }}>
          <Card>
            <Card.Body>
              <div className="structure-node-header mb-3">
                <h2 className="m-0">{titleText}</h2>
              </div>

              {error && <Alert variant="danger">{error}</Alert>}

              {loading ? (
                <div className="text-center my-3"><Spinner animation="border" /></div>
              ) : (
                <Form onSubmit={handleSubmit}>
                  <PageBasicFields
                    form={form}
                    setForm={setForm}
                    websiteId={id}
                    slugPlaceholder="e.g. my-first-post"
                    titlePlaceholder="Enter title"
                    preserveEmptySlug={isEdit}
                  />

                  <div className="form-row-horizontal mb-2">
                    <Form.Label>Body</Form.Label>
                    <Editor data={editorData} onChange={handleEditorChange} websiteId={id} />
                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button type="submit" className="primary-button" disabled={saving}>
                      {saving ? (<><Spinner animation="border" size="sm" className="me-2" /> Saving...</>) : 'Save'}
                    </Button>
                    <Button className="primary-button" onClick={handleCancel} disabled={saving}>Cancel</Button>
                  </div>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default ArticleEditor;
